import { type FC, useEffect } from "react";
import { type IntlShape, useIntl } from "react-intl";

import { useFusionMachine } from "@/hooks/machines/fusion";
import type { ErrorsAndWarningsProps, MachineFaultDisplay } from "@/schemas";

type FusionError = {
	message: string;
	arguments: (string | number)[];
};

type FusionErrorDisplay = {
	translationKey: string;
	resolutionArguments: Record<string, number | string>;
};

const toolAxisTranslationKeys: Record<number, string> = {
	4007: "ToolAxisError EngineStall",
	4008: "ToolAxisError PositiveLimitSwitchReached",
	4009: "ToolAxisError NegativeLimitSwitchReached",
	5003: "ToolAxisError PositiveSoftwareLimitReached",
	5004: "ToolAxisError NegativeSoftwareLimitReached",
	5026: "ToolAxisError SpeedExceedsLimit",
	5027: "ToolAxisError AccelerationExceedsLimit",
	7215: "ToolAxisError PowerlineFailure",
};

const getToolAxisTranslationKey = (errorArgument: string | number): string => {
	const errorCode = toolAxisTranslationKeys[Number(errorArgument)];

	if (!errorCode) {
		return "ToolAxisError";
	}

	return errorCode;
};

const longheadPositionTranslationKeys: Record<number, string> = {
	0: "LongHeadPosition LoweringRoller",
	1: "LongHeadPosition NoSignalAtConnection",
	2: "LongHeadPosition NoSignalAtDisconnection",
	3: "LongHeadPosition OutsideOfDriftingLimit",
};

const getLongheadPositionTranslationKey = (errorArgument: string | number): string => {
	const errorCode = longheadPositionTranslationKeys[Number(errorArgument)];

	if (!errorCode) {
		return "LongHeadPosition";
	}

	return errorCode;
};

function createError(error: FusionError): FusionErrorDisplay {
	const errorDisplay: FusionErrorDisplay = {
		translationKey: error.message,
		resolutionArguments: {},
	};

	if (error.message === "CorrugateMismatch") {
		return {
			...errorDisplay,
			resolutionArguments: { track: error.arguments[0], corrugate: error.arguments[1] },
		};
	}

	if (error.message === "OutOfCorrugate") {
		return {
			...errorDisplay,
			resolutionArguments: { track: error.arguments[0] },
		};
	}

	if (error.message === "ToolAxisError") {
		const codes = error.arguments.filter((n) => n !== "0").join(", ");

		return {
			translationKey: getToolAxisTranslationKey(error.arguments[0]),
			resolutionArguments: { code: codes },
		};
	}

	if (error.message === "LongHeadPosition") {
		return {
			translationKey: getLongheadPositionTranslationKey(error.arguments[0]),
			resolutionArguments: { longhead: error.arguments[1] },
		};
	}

	return errorDisplay;
}

function getTitle(item: FusionErrorDisplay, intl: IntlShape): string {
	const translationKey = `Fusion Error ${item.translationKey}`;

	return intl.formatMessage({ id: translationKey, defaultMessage: "{translationKey}" }, { translationKey });
}

function getResolution(item: FusionErrorDisplay, intl: IntlShape): string | undefined {
	const translationKey = `Fusion Action ${item.translationKey}`;

	const resolution = intl.formatMessage(
		{ id: translationKey, defaultMessage: "{translationKey}" },
		{ translationKey, ...item.resolutionArguments },
	);

	return resolution === translationKey ? undefined : resolution;
}

function createDisplayItem(machineId: string, error: FusionError, intl: IntlShape): MachineFaultDisplay {
	const item = createError(error);
	const imageName = error.message.replaceAll(" ", "").toLowerCase();

	return {
		machineId,
		title: getTitle(item, intl),
		resolution: getResolution(item, intl),
		image: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/fusion/error/${imageName}.png`,
		fallbackImage: `${import.meta.env?.VITE_BASENAME}/images/errors-and-warnings/fusion/error/default.png`,
		type: "Error",
	};
}

interface FusionMachineErrorsAndWarningsProps {
	readonly machineId: string;
	readonly onErrorsChanged: ErrorsAndWarningsProps["onErrorsChanged"];
}

const FusionMachineErrorsAndWarnings = ({ machineId, onErrorsChanged }: FusionMachineErrorsAndWarningsProps) => {
	const intl = useIntl();
	const { data: machine } = useFusionMachine(machineId, { refetchInterval: 5000 });

	useEffect(() => {
		if (machine === undefined) {
			return;
		}

		if (machine === null || machine.stopReason.isError === false) {
			onErrorsChanged(machineId, []);
			return;
		}

		onErrorsChanged(machine.id, [createDisplayItem(machine.id, machine.stopReason, intl)]);
	}, [intl, machine, machineId, onErrorsChanged]);

	return null;
};

export const FusionErrorsAndWarnings: FC<ErrorsAndWarningsProps> = ({ machines, onErrorsChanged }) => {
	return (
		<>
			{machines
				.filter(({ machineType: configuredMachineType }) => configuredMachineType === "Fusion")
				.map(({ machineId }) => (
					<FusionMachineErrorsAndWarnings key={machineId} machineId={machineId} onErrorsChanged={onErrorsChanged} />
				))}
		</>
	);
};
